import { useState } from "react";
import { Settings2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { money, fmt, Table, Td, PnL } from "../../../components/dashboard/Shared";

const STATUS = {
  due: "text-[#FF3B30] bg-[#FF3B30]/10", partial: "text-[#FFD600] bg-[#FFD600]/10",
  cleared: "text-[#00E676] bg-[#00E676]/10", waived: "text-gray-300 bg-white/10", na: "text-gray-500 bg-white/5",
};

export const ProfitShareLedger = ({ accountId, ledger, onChange, onMessage }) => {
  const [settings, setSettings] = useState(null);
  const [edit, setEdit] = useState(null);

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put(`/manager/mt5-clients/${accountId}/profit-share/settings`, { share_pct: parseFloat(settings.share_pct), opening_loss: parseFloat(settings.opening_loss) || 0 });
      onChange(data); setSettings(null); toast.success("Profit share settings saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };
  const saveWeek = async (e) => {
    e.preventDefault();
    try {
      const body = { status: edit.status, notes: edit.notes || null };
      if (edit.status === "partial") body.paid_amount = parseFloat(edit.paid_amount) || 0;
      const { data } = await api.put(`/manager/mt5-clients/${accountId}/profit-share/${edit.week_start}`, body);
      onChange(data); setEdit(null); toast.success(`Week ${edit.week_start} marked ${edit.status}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  if (!ledger) return <div className="skeleton h-40" />;
  return (
    <div className="space-y-4" data-testid="profit-share-ledger">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="card-dark p-4"><p className="label-xs">Share %</p><p className="tabular text-xl text-white mt-1" data-testid="share-pct">{ledger.share_pct}%</p></div>
        <div className="card-dark p-4"><p className="label-xs">Total Due</p><p className="tabular text-xl text-[#FF3B30] mt-1" data-testid="share-total-due">{money(ledger.total_due)}</p></div>
        <div className="card-dark p-4"><p className="label-xs">Total Cleared</p><p className="tabular text-xl text-[#00E676] mt-1" data-testid="share-total-cleared">{money(ledger.total_cleared)}</p></div>
        <div className="card-dark p-4"><p className="label-xs">Loss To Recover</p><p className={`tabular text-xl mt-1 ${ledger.loss_to_recover > 0 ? "text-[#FFD600]" : "text-gray-400"}`} data-testid="loss-to-recover">{money(ledger.loss_to_recover)}</p><p className="text-[10px] text-gray-600 mt-1">No share until recovered</p></div>
      </div>
      <div className="flex justify-between items-center">
        <p className="text-xs text-gray-500">Weeks run Monday → Sunday (UTC). Share applies only to profit above carried-forward loss.</p>
        <button data-testid="share-settings-btn" onClick={() => setSettings({ share_pct: ledger.share_pct, opening_loss: ledger.opening_loss })} className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2"><Settings2 size={12} /> Share Settings</button>
      </div>
      <Table headers={["Week", "Trades", "Net P/L", "Loss B/F", "Recovered", "Loss C/F", "Shareable", "Share", "Paid", "Status", ""]} testId="ledger-table">
        {ledger.weeks.map((w) => (
          <tr key={w.week_start} data-testid={`ledger-week-${w.week_start}`} className="border-b border-white/5 hover:bg-white/[0.03]">
            <Td className="text-xs tabular text-white">{w.week_start} → {w.week_end}{w.in_progress && <span className="ml-2 text-[9px] text-[#00F0FF]">LIVE</span>}</Td>
            <Td className="tabular">{w.trades}</Td>
            <Td><PnL value={w.net_profit} /></Td>
            <Td className="tabular text-xs text-gray-400">{fmt(w.loss_brought_forward)}</Td>
            <Td className="tabular text-xs text-[#00F0FF]">{fmt(w.loss_recovered)}</Td>
            <Td className="tabular text-xs text-[#FFD600]">{fmt(w.loss_carried_forward)}</Td>
            <Td className="tabular text-xs">{fmt(w.shareable_profit)}</Td>
            <Td className="tabular text-white" data-testid={`share-amount-${w.week_start}`}>{money(w.share_amount)}</Td>
            <Td className="tabular text-xs text-[#00E676]">{fmt(w.paid_amount)}</Td>
            <Td><span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${STATUS[w.status]}`} data-testid={`share-status-${w.week_start}`}>{w.status}</span></Td>
            <Td>
              <div className="flex gap-2">
                {w.share_amount > 0 && <button data-testid={`ledger-edit-${w.week_start}`} onClick={() => setEdit({ ...w, paid_amount: w.paid_amount || "" })} className="text-[11px] text-[#00F0FF] hover:underline">Update</button>}
                <button data-testid={`ledger-msg-${w.week_start}`} onClick={() => onMessage(w)} className="text-[11px] text-gray-400 hover:text-white flex items-center gap-1"><Send size={10} /> Message</button>
              </div>
            </Td>
          </tr>
        ))}
      </Table>

      {settings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setSettings(null)} data-testid="share-settings-modal">
          <form onSubmit={saveSettings} className="card-dark w-full max-w-sm p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <p className="text-white">Profit Share Settings</p>
            <div><label className="label-xs block mb-1">Share % (what you take from net profit)</label>
              <input data-testid="share-pct-input" type="number" min="0" max="100" step="1" className="input-dark" value={settings.share_pct} onChange={(e) => setSettings({ ...settings, share_pct: e.target.value })} /></div>
            <div><label className="label-xs block mb-1">Opening loss to recover ($, before platform history)</label>
              <input data-testid="opening-loss-input" type="number" min="0" step="0.01" className="input-dark" value={settings.opening_loss} onChange={(e) => setSettings({ ...settings, opening_loss: e.target.value })} /></div>
            <button data-testid="share-settings-save" className="btn-primary w-full py-2 text-sm">Save</button>
          </form>
        </div>
      )}
      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setEdit(null)} data-testid="week-status-modal">
          <form onSubmit={saveWeek} className="card-dark w-full max-w-sm p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <p className="text-white">Week {edit.week_start} · Share {money(edit.share_amount)}</p>
            <div className="grid grid-cols-2 gap-2">
              {["due", "partial", "cleared", "waived"].map((s) => (
                <button type="button" key={s} data-testid={`status-opt-${s}`} onClick={() => setEdit({ ...edit, status: s })} className={`py-2 rounded text-xs uppercase border ${edit.status === s ? "border-[#00F0FF] text-[#00F0FF]" : "border-white/10 text-gray-400"}`}>{s}</button>
              ))}
            </div>
            {edit.status === "partial" && <div><label className="label-xs block mb-1">Amount received ($)</label>
              <input data-testid="paid-amount-input" type="number" min="0" step="0.01" className="input-dark" value={edit.paid_amount} onChange={(e) => setEdit({ ...edit, paid_amount: e.target.value })} /></div>}
            <div><label className="label-xs block mb-1">Notes (UTR / remarks)</label>
              <input data-testid="week-notes-input" className="input-dark" value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} /></div>
            <button data-testid="week-status-save" className="btn-primary w-full py-2 text-sm">Save</button>
          </form>
        </div>
      )}
    </div>
  );
};
